import uniqid from 'uniqid';
import GeneralSelectorTableStore, {NoneKPIColumns, SelectorTableColumnDef} from '../GeneralSelectorTableStore';
import {SelectorTableLayout} from '../SelectorTableLayout';
import * as InsightsAPI from '../../apis/InsightsAPI';
import {numberFormat, percentFormat, textFormat} from '../../utility/FormatValueUtils';
import {AnalyticsTab} from '../store';
import {DescriptiveKPIAdditions, KPI, KPIGroupKey, ProductKPI, SearchtermKPI, SearchtermSlopeOutlierKPI, SessionInsightKPI} from '../../kpi';
import {TableDataForChannel} from '../../types/api/tableData/TableDataForChannel';
import {MessageProvider} from '../../i18n/i18n';
import {Omit} from '../../types/Utility';
import {Channel} from '../../types/Channel';
import {ChannelState} from '../ChannelStore';
import {TimeState} from '../TimeIntervalStore';
import {DateUtils} from '../../utility/DateUtils';
import {TableSort, TableSortDirection} from '../../types/Table';
import {DataForPrimaryAndSecondaryAttribute} from '../InsightsTableStore';
import {ProductsFromSearchtermResponse} from '../../types/api/insights/ProductsFromSearchterms';

type SelectorTableColumnDefWithoutLabel = Omit<SelectorTableColumnDef, 'label' | 'width'>;

export class SearchSelectorTableStore extends GeneralSelectorTableStore {
    constructor(protected mainStore, t: MessageProvider, protected readonly channelState: ChannelState, timeState: TimeState) {
        super(mainStore, t, channelState, timeState);
        this.updateTableSettingsFromLocalStorage('searchterm');
    }

    protected dependantAnalyticsTab: AnalyticsTab = AnalyticsTab.SearchView;

    public readonly defaultSorting: TableSort = {
        sortDirection: TableSortDirection.DESC,
        attribute: SearchtermKPI.SalesFromSearches,
    };

    public tableSorting: TableSort = {...this.defaultSorting};

    private fixedColumnDef(): SelectorTableColumnDefWithoutLabel[] {
        return [
            {
                dataKey: NoneKPIColumns.RowNumber,
                minWidth: 50,
                align: 'right',
                isSortable: false,
                valueFormat: textFormat(),
                channelComparisonEnabled: false,
            },
            {
                dataKey: KPIGroupKey.Searchterm,
                minWidth: 300,
                align: 'left',
                valueFormat: textFormat(),
                withRevealOnHover: true,
                channelComparisonEnabled: false,
                onIconClick: this.showCockpitLinks() ? (cellValue) => this.mainStore.navigateToCockpit(cellValue) : undefined,
            },
        ];
    }

    private showCockpitLinks(): boolean {
        return window.parent !== window && this.mainStore.showCockpitLinks;
    }

    private funnelColumnDef(): SelectorTableColumnDefWithoutLabel[] {
        return [
            {
                dataKey: SearchtermKPI.SearchCount,
                sessionApiKey: SessionInsightKPI.SearchEvents,
                minWidth: 120,
                align: 'right',
                scoreAttributes: {
                    slope: SearchtermSlopeOutlierKPI.SearchSlopeValue,
                    outlier: SearchtermSlopeOutlierKPI.SearchOutlierScore,
                    maxSlope: SearchtermSlopeOutlierKPI.MaxSearchSlopeValue,
                    minSlope: SearchtermSlopeOutlierKPI.MinSearchSlopeValue,
                },
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.ClickCount,
                sessionApiKey: SearchtermKPI.ClickCount,
                minWidth: 120,
                align: 'right',
                scoreAttributes: {
                    slope: SearchtermSlopeOutlierKPI.ClickSlopeValue,
                    outlier: SearchtermSlopeOutlierKPI.ClickOutlierScore,
                    maxSlope: SearchtermSlopeOutlierKPI.MaxClickSlopeValue,
                    minSlope: SearchtermSlopeOutlierKPI.MinClickSlopeValue,
                },
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.AsnClickCount,
                minWidth: 120,
                align: 'right',
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.NaviClickCount,
                minWidth: 120,
                align: 'right',
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.UniqueClickCount,
                minWidth: 120,
                align: 'right',
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.CartCount,
                sessionApiKey: SessionInsightKPI.CartTotalAmount,
                minWidth: 120,
                align: 'right',
                scoreAttributes: {
                    slope: SearchtermSlopeOutlierKPI.CartSlopeValue,
                    outlier: SearchtermSlopeOutlierKPI.CartOutlierScore,
                    maxSlope: SearchtermSlopeOutlierKPI.MaxCartSlopeValue,
                    minSlope: SearchtermSlopeOutlierKPI.MinCartSlopeValue,
                },
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.CheckoutCount,
                sessionApiKey: SessionInsightKPI.CheckoutTotalAmount,
                minWidth: 120,
                align: 'right',
                scoreAttributes: {
                    slope: SearchtermSlopeOutlierKPI.CheckoutSlopeValue,
                    outlier: SearchtermSlopeOutlierKPI.CheckoutOutlierScore,
                    maxSlope: SearchtermSlopeOutlierKPI.MaxCheckoutSlopeValue,
                    minSlope: SearchtermSlopeOutlierKPI.MinCheckoutSlopeValue,
                },
                valueFormat: numberFormat(),
                isChartable: true,
                withFilter: true,
            },
        ];
    }

    private ratesColumnDef(): SelectorTableColumnDefWithoutLabel[] {
        return [
            {
                dataKey: SearchtermKPI.ClickRate,
                minWidth: 120,
                align: 'right',
                valueFormat: percentFormat(),
                isRate: true,
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.ClickToCartRate,
                minWidth: 100,
                align: 'right',
                valueFormat: percentFormat(),
                isRate: true,
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.CartToCheckoutRate,
                minWidth: 100,
                align: 'right',
                isRate: true,
                isChartable: true,
                valueFormat: percentFormat(),
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.UniqueClickRate,
                minWidth: 130,
                align: 'right',
                valueFormat: percentFormat(),
                isRate: true,
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.ClickToBuyRate,
                minWidth: 125,
                align: 'right',
                valueFormat: percentFormat(),
                isRate: true,
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.CheckoutPerSearchRate,
                minWidth: 100,
                isChartable: true,
                align: 'right',
                valueFormat: percentFormat(),
                withFilter: true,
                isRate: true,
            },
        ];
    }

    private salesKpiColumnDef(): SelectorTableColumnDefWithoutLabel[] {
        return [
            {
                dataKey: SearchtermKPI.SalesFromSearches,
                sessionApiKey: 'sales',
                minWidth: 140,
                align: 'right',
                scoreAttributes: {
                    slope: SearchtermSlopeOutlierKPI.SalesFromSearchesSlopeValue,
                    outlier: SearchtermSlopeOutlierKPI.SalesFromSearchesOutlierScore,
                    maxSlope: SearchtermSlopeOutlierKPI.MaxSalesFromSearchesSlopeValue,
                    minSlope: SearchtermSlopeOutlierKPI.MinSalesFromSearchesSlopeValue,
                },
                usesMoneyFormat: true,
                valueFormat: this.getMoneyFormatter(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.SalesFromSearchesPerSearch,
                minWidth: 150,
                align: 'right',
                usesMoneyFormat: true,
                valueFormat: this.getMoneyFormatter(),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.SalesFromSearchesPerClick,
                minWidth: 150,
                align: 'right',
                usesMoneyFormat: true,
                valueFormat: this.getMoneyFormatter(),
                isChartable: true,
                withFilter: true,
            },
        ];
    }

    private miscColumnDef(): SelectorTableColumnDefWithoutLabel[] {
        return [
            {
                dataKey: SearchtermKPI.AvgMatchCount,
                minWidth: 120,
                align: 'right',
                valueFormat: numberFormat(2),
                isChartable: true,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.AvgBestSimi,
                minWidth: 150,
                align: 'right',
                valueFormat: numberFormat(),
                isRate: false,
                withFilter: true,
            },
            {
                dataKey: SearchtermKPI.AvgPos,
                minWidth: 125,
                align: 'right',
                valueFormat: numberFormat(),
                withFilter: true,
            },
        ];
    }

    public get tableColumnList(): SelectorTableColumnDef[] {
        return this.resizeColumns(
            this.withI18N([
                ...this.fixedColumnDef(),
                ...this.funnelColumnDef(),
                ...this.ratesColumnDef(),
                ...this.salesKpiColumnDef(),
                ...this.miscColumnDef(),
            ])
        );
    }

    public set tableColumnList(_: SelectorTableColumnDef[]) {
        // Dummy
    }

    public readonly layout = new SelectorTableLayout(
        this.tableColumns,
        () => this.mainStore.compareStore,
        [
            {
                name: 'fixed',
                label: '',
                columns: [NoneKPIColumns.RowNumber, KPIGroupKey.Searchterm],
            },
            {
                name: 'funnel',
                label: this.t('table:table.funnel'),
                columns: [
                    SearchtermKPI.SearchCount,
                    SearchtermKPI.ClickCount,
                    SearchtermKPI.AsnClickCount,
                    SearchtermKPI.NaviClickCount,
                    SearchtermKPI.UniqueClickCount,
                    SearchtermKPI.CartCount,
                    SearchtermKPI.CheckoutCount,
                ],
                hiddenColumns: [SearchtermKPI.AsnClickCount, SearchtermKPI.NaviClickCount, SearchtermKPI.UniqueClickCount],
            },
            {
                name: 'rates',
                label: this.t('table:table.rates'),
                columns: [
                    SearchtermKPI.ClickRate,
                    SearchtermKPI.ClickToCartRate,
                    SearchtermKPI.CartToCheckoutRate,
                    SearchtermKPI.ClickToBuyRate,
                    SearchtermKPI.CheckoutPerSearchRate,
                    SearchtermKPI.UniqueClickRate,
                ],
                hiddenColumns: [SearchtermKPI.UniqueClickRate],
            },
            {
                name: 'salesKpis',
                label: this.t('table:table.salesKpis'),
                columns: [SearchtermKPI.SalesFromSearches, SearchtermKPI.SalesFromSearchesPerSearch, SearchtermKPI.SalesFromSearchesPerClick],
            },
            {
                name: 'searchResults',
                label: this.t('table:table.searchResults'),
                columns: [SearchtermKPI.AvgMatchCount, SearchtermKPI.AvgBestSimi, SearchtermKPI.AvgPos],
                hiddenColumns: [SearchtermKPI.AvgPos],
            },
        ],
        this.dependantAnalyticsTab,
        this.saveTableLayoutToLocalStorage('searchterm'),
        this.getTableLayoutFromLocalStorage('searchterm'),
        this.recomputeMultiGridSizeIfPossible
    );

    public fetchInsightsData(searchterm: string, kpi: KPI, channel: Channel): void {
        const {insightsTableStore: insightsTableStore} = this.mainStore;
        InsightsAPI.productsFromSearchTerm({
            searchterm,
            channel: channel.name,
            interval: DateUtils.toHourlyDateRange(this.mainStore.timeIntervalStore.mainDateRange),
        })
            .then((resp) => {
                const convertedSearchtermsForProductId = this.convertSearchtermsForProductToFitFrontend(resp.data);
                insightsTableStore.insightsTableData = {
                    loading: false,
                    data: insightsTableStore.sortForPrimaryAndSecondaryAttribute(convertedSearchtermsForProductId),
                };
            })
            .catch(() => {
                insightsTableStore.insightsTableData = {
                    loading: false,
                    data: [],
                };
            });
    }

    private convertSearchtermsForProductToFitFrontend(
        searchtermsForProduct: ProductsFromSearchtermResponse[]
    ): DataForPrimaryAndSecondaryAttribute[] {
        return searchtermsForProduct.map((singleProduct) => {
            return {
                id: uniqid(),
                [SessionInsightKPI.SearchEvents]: singleProduct.kpis[SessionInsightKPI.SearchEvents],
                [SessionInsightKPI.CartEvents]: singleProduct.kpis[SessionInsightKPI.CartEvents],
                [SessionInsightKPI.CartTotalAmount]: singleProduct.kpis[SessionInsightKPI.CartTotalAmount],
                [SessionInsightKPI.CheckoutEvents]: singleProduct[SessionInsightKPI.CheckoutEvents],
                [SessionInsightKPI.CheckoutTotalAmount]: singleProduct.kpis[SessionInsightKPI.CheckoutTotalAmount],
                [SearchtermKPI.ClickCount]: singleProduct.kpis[SearchtermKPI.ClickCount],
                [ProductKPI.Sales]: singleProduct.kpis[ProductKPI.Sales],
                [KPIGroupKey.Searchterm]:
                    singleProduct[DescriptiveKPIAdditions.Title] === null
                        ? singleProduct[KPIGroupKey.ProductId]
                        : singleProduct[DescriptiveKPIAdditions.Title],
            };
        });
    }

    public calculateRatesForStickySumRow(
        data: TableDataForChannel,
        channel: Channel,
        searchterm: string,
        variantCompareActive = false
    ): TableDataForChannel {
        const newData = {...data, [NoneKPIColumns.RowNumber]: ''};

        if (searchterm !== '') {
            this.channelSumRowIsChartable = false;
            newData[KPIGroupKey.Searchterm] = this.t('table:table.allSearchterm', {searchterm});
            return newData;
        }

        if (data.channelCompareRegister) {
            const channelNames = Object.keys(data.channelCompareRegister);
            this.channelSumRowIsChartable = true;
            const channelKey = variantCompareActive ? 'table:variantWithText' : 'table:channelWithText';
            newData[KPIGroupKey.Searchterm] = this.t(channelKey, {channel: channelNames.join(', '), count: channelNames.length});
            return newData;
        }

        this.channelSumRowIsChartable = true;
        newData[KPIGroupKey.Searchterm] = this.t('table:channelWithText', {channel: channel.name});
        return newData;
    }
}

export default SearchSelectorTableStore;



// WEBPACK FOOTER //
// ./src/stores/searchterm/SearchSelectorTableStore.ts